/**
 * Helpers to resolve locale-specific fields from data constants.
 *
 * Data files under `@/content/*.ts` keep Russian copy on the base
 * field (`title`, `description`, …) and English copy on a parallel
 * `*En` field (`titleEn`, `descriptionEn`, …). Components that render
 * those fields thread a `Locale` through and call these helpers to
 * pick the right string.
 *
 * Falls back to the base (RU) value when:
 *   - locale is not `'en'`;
 *   - the `*En` field is missing, empty or not a string/array.
 *
 * Covered by `tests/lib-localize.spec.ts`.
 */

import type { Locale } from '@/i18n/locales';

function enKey<F extends string>(field: F): `${F}En` {
  return `${field}En` as `${F}En`;
}

/**
 * Pick a localised string field from an item.
 */
export function pickLocalized<F extends string>(
  item: Partial<Record<F | `${F}En`, string>>,
  field: F,
  locale: Locale,
): string {
  if (locale === 'en') {
    const record = item as unknown as Record<string, unknown>;
    const candidate = record[enKey(field)];
    if (typeof candidate === 'string' && candidate.length > 0) return candidate;
  }
  const base = (item as unknown as Record<string, unknown>)[field];
  return typeof base === 'string' ? base : '';
}

/**
 * Pick a localised string-array field from an item.
 */
export function pickLocalizedArray<F extends string>(
  item: Partial<Record<F | `${F}En`, readonly string[]>>,
  field: F,
  locale: Locale,
): readonly string[] {
  if (locale === 'en') {
    const record = item as unknown as Record<string, unknown>;
    const candidate = record[enKey(field)];
    if (Array.isArray(candidate) && candidate.every((v) => typeof v === 'string')) {
      return candidate as readonly string[];
    }
  }
  const base = (item as unknown as Record<string, unknown>)[field];
  if (Array.isArray(base) && base.every((v) => typeof v === 'string')) {
    return base as readonly string[];
  }
  return [];
}

/** Часть секции FAQ: пара вопрос / ответ на RU и EN. */
export interface FaqItemPair {
  readonly title: string;
  readonly answer: string;
}

/**
 * Локализация `items` секции `kind: 'faq'` (массив пар title/answer + `itemsEn`).
 */
export function pickLocalizedFaqItems(
  section: {
    readonly items?: readonly FaqItemPair[];
    readonly itemsEn?: readonly FaqItemPair[];
  },
  locale: Locale,
): readonly FaqItemPair[] {
  const base = section.items ?? [];
  if (locale !== 'en') return base;
  const en = section.itemsEn;
  if (!en || en.length === 0) return base;
  return base.map((ru, i) => {
    const pair = en[i];
    if (!pair) return ru;
    const title =
      typeof pair.title === 'string' && pair.title.length > 0 ? pair.title : ru.title;
    const answer =
      typeof pair.answer === 'string' && pair.answer.length > 0 ? pair.answer : ru.answer;
    return { title, answer };
  });
}

type LocalizedStringKeys<KS extends readonly string[]> = {
  [K in KS[number]]: string;
};

type LocalizedStringArrayKeys<KA extends readonly string[]> = {
  [K in KA[number]]: readonly string[];
};

type CopyKeys<T extends Record<string, unknown>, K extends readonly (keyof T)[]> = Pick<
  T,
  K[number] & keyof T
>;

/**
 * Преобразует массив записей с полями RU / `*En` в массив строковых представлений
 * для текущей локали.
 *
 * - `copy` — ключи, которые копируются как есть (id, kind, …).
 * - `strings` — имена строковых полей; для каждого вызывается `pickLocalized`.
 * - `arrays` — имена полей-массивов строк; для каждого — `pickLocalizedArray`.
 *
 * Поля из `strings` / `arrays` должны следовать контракту `*En`, как у
 * `pickLocalized` / `pickLocalizedArray`. Нелокализуемые поля указывайте только в
 * `copy`.
 */
export function mapLocalizedRecords<
  T extends Record<string, unknown>,
  const KCopy extends readonly (keyof T)[],
  const KS extends readonly string[],
  const KA extends readonly string[] = [],
>(
  items: readonly T[],
  locale: Locale,
  spec: {
    readonly copy: KCopy;
    readonly strings: KS;
    readonly arrays?: KA;
  },
): ReadonlyArray<
  CopyKeys<T, KCopy> & LocalizedStringKeys<KS> & LocalizedStringArrayKeys<KA>
> {
  const arrayFields = spec.arrays ?? ([] as unknown as KA);
  return items.map((item) => {
    const row: Record<string, unknown> = {};
    for (const key of spec.copy) {
      row[key as string] = item[key];
    }
    for (const field of spec.strings) {
      row[field] = pickLocalized(item as never, field, locale);
    }
    for (const field of arrayFields) {
      row[field] = pickLocalizedArray(item as never, field, locale);
    }
    return row as CopyKeys<T, KCopy> & LocalizedStringKeys<KS> & LocalizedStringArrayKeys<KA>;
  });
}
